import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useT } from '../lib/i18n';
import { ArrowLeft, Shield } from 'lucide-react';

export default function Privacy() {
  const { lang } = useT();
  const navigate = useNavigate();
  return (
    <div className="min-h-screen bg-white">
      <header className="sticky top-0 bg-white/95 backdrop-blur-xl border-b border-[#E5DCC9] z-10 pt-safe">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 h-16 flex items-center gap-3">
          <button
            type="button"
            onClick={() => { if (window.history.length > 1) navigate(-1); else navigate('/'); }}
            className="inline-flex items-center gap-1 text-sm font-bold text-gray-600"
            data-testid="privacy-back-btn"
          >
            <ArrowLeft size={16} /> {lang === 'fr' ? 'Retour' : 'Back'}
          </button>
        </div>
      </header>
      <main className="max-w-2xl mx-auto px-4 sm:px-6 py-8">
        <div className="flex items-center gap-2 mb-4">
          <Shield size={20} className="text-[#7A1F2B]" />
          <div className="text-[11px] font-bold uppercase tracking-[0.2em] text-gray-500">
            {lang === 'fr' ? 'Document légal' : 'Legal document'}
          </div>
        </div>
        <h1 className="font-heading text-4xl font-black tracking-tighter mb-2">
          {lang === 'fr' ? 'Politique de confidentialité' : 'Privacy Policy'}
        </h1>
        <p className="text-xs text-gray-500 mb-8">
          {lang === 'fr' ? 'Conforme à la Loi 25 du Québec · Dernière mise à jour : 26 juin 2026' : 'Compliant with Quebec Law 25 · Updated: June 26, 2026'}
        </p>

        {lang === 'fr' ? <FrenchPolicy /> : <EnglishPolicy />}
      </main>
    </div>
  );
}

function FrenchPolicy() {
  return (
    <div className="space-y-6 text-gray-700">
      <Section title="1. Responsable du traitement des renseignements personnels">
        <b>17953071 CANADA INC.</b> (exploitant SoumissionPro) est responsable du traitement des renseignements
        personnels collectés via le Service. Pour toute question, communiquez avec notre responsable de la
        protection des renseignements personnels :
        <ul className="list-disc pl-5 mt-2 space-y-1">
          <li>Courriel : <b>support@soumissionpro.net</b></li>
          <li>Téléphone : <b>438-792-2033</b></li>
          <li>Adresse : Chambly, Québec, Canada</li>
        </ul>
      </Section>

      <Section title="2. Renseignements collectés">
        Nous collectons les renseignements suivants :
        <ul className="list-disc pl-5 mt-2 space-y-1">
          <li>Informations de compte : courriel, mot de passe (chiffré), nom d'entreprise, numéro RBQ</li>
          <li>Informations professionnelles : adresse, téléphone, logo, province</li>
          <li>Données de soumissions : clients, matériaux, prix, signatures</li>
          <li>Données techniques : adresse IP, navigateur, modèle d'appareil (pour le bon fonctionnement)</li>
          <li>Données de localisation générales (province) pour ajustement des prix régionaux</li>
        </ul>
      </Section>

      <Section title="3. Finalités de la collecte">
        Vos renseignements sont utilisés pour : (a) fournir le Service; (b) générer vos soumissions et PDF;
        (c) ajuster les prix de matériaux selon votre région (via IA Claude); (d) envoyer des notifications de
        signature; (e) améliorer le Service; (f) respecter nos obligations légales.
      </Section>

      <Section title="4. Consentement">
        En créant un compte, vous consentez à la collecte et au traitement de vos renseignements aux fins
        décrites. Vous pouvez retirer votre consentement à tout moment en supprimant votre compte.
      </Section>

      <Section title="5. Partage avec des tiers">
        Nous ne vendons jamais vos données. Nous partageons uniquement les renseignements nécessaires avec :
        <ul className="list-disc pl-5 mt-2 space-y-1">
          <li><b>Supabase</b> (hébergement de données et authentification, conforme RGPD)</li>
          <li><b>Anthropic Claude</b> (IA pour ajustement de prix; descriptions de matériaux uniquement, jamais les données clients)</li>
          <li><b>Resend</b> (envoi de courriels transactionnels, si activé)</li>
          <li><b>Stripe</b> (traitement des paiements pour les abonnements via le web; les données de carte ne transitent jamais par nos serveurs)</li>
          <li><b>Apple In-App Purchase</b> et <b>RevenueCat</b> (traitement et gestion des abonnements sur iOS; conformité Apple Guideline 3.1.1)</li>
          <li>Les destinataires de vos soumissions (clients que vous choisissez d'informer)</li>
        </ul>
      </Section>

      <Section title="6. Hébergement et localisation">
        Vos données sont hébergées sur les serveurs de Supabase. Vous reconnaissez et acceptez que vos
        renseignements puissent être transférés et traités aux États-Unis et au Canada, sous réserve des
        protections contractuelles standard (clauses contractuelles type).
      </Section>

      <Section title="7. Durée de conservation">
        Vos renseignements sont conservés tant que votre compte est actif. Après suppression du compte, les
        données sont conservées 30 jours en sauvegarde, puis détruites de façon sécuritaire, sauf obligations
        légales contraires (ex. : facturation, fiscalité).
      </Section>

      <Section title="8. Vos droits (Loi 25)">
        Conformément à la Loi 25 du Québec, vous avez le droit :
        <ul className="list-disc pl-5 mt-2 space-y-1">
          <li>D'accéder à vos renseignements personnels</li>
          <li>De les rectifier s'ils sont inexacts</li>
          <li>De les supprimer (droit à l'oubli)</li>
          <li>De portabilité (recevoir vos données dans un format structuré)</li>
          <li>De désindexation</li>
          <li>De déposer une plainte auprès de la Commission d'accès à l'information du Québec (CAI)</li>
        </ul>
        Pour exercer ces droits : <b>support@soumissionpro.net</b>
      </Section>

      <Section title="9. Sécurité">
        Nous mettons en œuvre des mesures techniques et organisationnelles raisonnables : chiffrement TLS,
        authentification sécurisée, sauvegardes régulières, contrôle d'accès strict. Aucun système n'est
        infaillible à 100 %, mais nous nous engageons à notifier la CAI et les utilisateurs concernés dans les
        72 heures suivant la découverte d'un incident de sécurité affectant vos données.
      </Section>

      <Section title="10. Cookies et technologies similaires">
        Nous utilisons des cookies essentiels pour le fonctionnement du Service (session, préférences). Aucun
        cookie de suivi publicitaire tiers n'est installé.
      </Section>

      <Section title="11. Mineurs">
        Le Service est destiné aux entrepreneurs majeurs (18 ans et plus). Nous ne collectons pas sciemment de
        données de mineurs.
      </Section>

      <Section title="12. Modifications de la politique">
        Toute modification importante sera notifiée par courriel ou dans l'application au moins 30 jours avant
        son entrée en vigueur.
      </Section>
    </div>
  );
}

function EnglishPolicy() {
  return (
    <div className="space-y-6 text-gray-700">
      <Section title="1. Data Controller">
        <b>17953071 CANADA INC.</b> (operating SoumissionPro) is the data controller. Contact our Privacy Officer:
        <ul className="list-disc pl-5 mt-2 space-y-1">
          <li>Email: <b>support@soumissionpro.net</b></li>
          <li>Phone: <b>+1 438-792-2033</b></li>
          <li>Address: Chambly, Quebec, Canada</li>
        </ul>
      </Section>
      <Section title="2. Data Collected">
        Account info, professional info, quote data, technical/device data, general location (province).
      </Section>
      <Section title="3. Purpose">
        Provide the Service, generate PDFs, AI price tuning, signature notifications, improve the Service, legal compliance.
      </Section>
      <Section title="4. Consent">
        By creating an account you consent to these uses. Withdraw anytime by deleting your account.
      </Section>
      <Section title="5. Third Parties">
        We never sell your data. Limited sharing with:
        <ul className="list-disc pl-5 mt-2 space-y-1">
          <li><b>Supabase</b> (data hosting and authentication)</li>
          <li><b>Anthropic Claude</b> (AI; material descriptions only, never client data)</li>
          <li><b>Resend</b> (transactional emails, if enabled)</li>
          <li><b>Stripe</b> (web subscription payment processing; card data never transits our servers)</li>
          <li><b>Apple In-App Purchase</b> and <b>RevenueCat</b> (iOS subscription processing and management; Apple Guideline 3.1.1 compliance)</li>
          <li>Recipients you choose to share quotes with</li>
        </ul>
      </Section>
      <Section title="6. Hosting Location">
        Data hosted on Supabase (US/Canada). Standard contractual clauses apply.
      </Section>
      <Section title="7. Retention">
        Kept while your account is active; 30-day backup after deletion.
      </Section>
      <Section title="8. Your Rights (Quebec Law 25)">
        Access, rectify, delete, portability, deindexing, complaint to Quebec CAI. Email <b>support@soumissionpro.net</b>.
      </Section>
      <Section title="9. Security">
        TLS encryption, secure auth, backups, access control. Incidents notified within 72 hours.
      </Section>
      <Section title="10. Cookies">
        Essential cookies only. No third-party ad tracking.
      </Section>
      <Section title="11. Minors">
        Service is for adults (18+). We do not knowingly collect minors' data.
      </Section>
      <Section title="12. Changes">
        Material changes notified by email at least 30 days before effect.
      </Section>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section>
      <h2 className="font-heading font-black text-lg mb-2 text-[#7A1F2B]">{title}</h2>
      <div className="text-sm leading-relaxed">{children}</div>
    </section>
  );
}
